import type { UseMutationResult } from '@tanstack/react-query'

import { FxIcon } from '@/components/fx'

/**
 * What happened to the last save, beside the control that triggered it.
 *
 * This build mounts no Toaster, and a toast would be the wrong place
 * anyway: a settings tab has half a dozen independent controls, and
 * "Speichern fehlgeschlagen" in a corner does not say which one. The line
 * sits under its own control and is a live region, so a screen reader hears
 * it without moving focus.
 */
export function SaveHint({
  mutation,
  saved = 'Gespeichert',
  failed = 'Konnte nicht gespeichert werden.',
  className,
}: {
  mutation: Pick<UseMutationResult<unknown, unknown, never>, 'isPending' | 'isSuccess' | 'isError'>
  saved?: string
  failed?: string
  className?: string
}) {
  const busy = mutation.isPending
  const ok = !busy && mutation.isSuccess
  const bad = !busy && mutation.isError

  return (
    <p
      role="status"
      aria-live="polite"
      className={['min-h-[1.45em] text-small', bad ? 'text-danger-text' : 'text-text-muted', className]
        .filter(Boolean)
        .join(' ')}
    >
      {busy && 'Wird gespeichert …'}
      {ok && (
        <span className="inline-flex items-center gap-1">
          <FxIcon name="check" size={16} />
          {saved}
        </span>
      )}
      {bad && failed}
    </p>
  )
}
